import { peopleAnalyticsApi } from "@/api/reporting/people-analytics.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ApiErrorException } from "@/types/api.types.ts";
import {
  ApiPeopleAnalyticsRowsInput,
  ApiPeopleAnalyticsWidgetDataInput,
  ApiPeopleAnalyticsWidgetSaveInput,
} from "@/types/reporting/people-analytics.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const peopleAnalyticsKeys = createQueryKeys("people-analytics", {
  columns: {
    queryKey: null,
    queryFn: async ({ signal }) => {
      const result = await peopleAnalyticsApi.columns(signal);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  },
  rows: (input: ApiPeopleAnalyticsRowsInput) => ({
    queryKey: [input],
    queryFn: async ({ signal }) => {
      const result = await peopleAnalyticsApi.rows(input, signal);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  cacheState: {
    queryKey: null,
    queryFn: async ({ signal }) => {
      const result = await peopleAnalyticsApi.cacheState(signal);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  },
  dashboards: {
    queryKey: null,
    queryFn: async ({ signal }) => {
      const result = await peopleAnalyticsApi.dashboards(signal);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  },
  widgets: {
    queryKey: null,
    queryFn: async ({ signal }) => {
      const result = await peopleAnalyticsApi.widgets(signal);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  },
  widgetData: (widgetId: number, input: ApiPeopleAnalyticsWidgetDataInput) => ({
    queryKey: [widgetId, input],
    queryFn: async ({ signal }) => {
      const result = await peopleAnalyticsApi.widgetData(widgetId, input, signal);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
});

export const usePeopleAnalyticsColumns = () => {
  return useQuery({ ...peopleAnalyticsKeys.columns });
};

export const usePeopleAnalyticsRows = (
  input: ApiPeopleAnalyticsRowsInput,
  enabled: boolean = true
) => {
  return useQuery({
    ...peopleAnalyticsKeys.rows(input),
    enabled,
  });
};

export const usePeopleAnalyticsDashboards = (enabled: boolean = true) => {
  return useQuery({ ...peopleAnalyticsKeys.dashboards, enabled });
};

export const usePeopleAnalyticsWidgets = (enabled: boolean = true) => {
  return useQuery({ ...peopleAnalyticsKeys.widgets, enabled });
};

export const useCreatePeopleAnalyticsWidget = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async (input: ApiPeopleAnalyticsWidgetSaveInput) => {
      const result = await peopleAnalyticsApi.createWidget(input);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: peopleAnalyticsKeys.dashboards.queryKey });
      queryClient.invalidateQueries({ queryKey: peopleAnalyticsKeys.widgets.queryKey });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useUpdatePeopleAnalyticsWidget = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async ({
      widgetId,
      input,
    }: {
      widgetId: number;
      input: ApiPeopleAnalyticsWidgetSaveInput;
    }) => {
      const result = await peopleAnalyticsApi.updateWidget(widgetId, input);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: peopleAnalyticsKeys.dashboards.queryKey });
      queryClient.invalidateQueries({ queryKey: peopleAnalyticsKeys.widgets.queryKey });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useDeletePeopleAnalyticsWidget = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async (widgetId: number) => {
      const result = await peopleAnalyticsApi.deleteWidget(widgetId);
      if (result.error) throw new ApiErrorException(result.error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: peopleAnalyticsKeys.dashboards.queryKey });
      queryClient.invalidateQueries({ queryKey: peopleAnalyticsKeys.widgets.queryKey });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const usePeopleAnalyticsWidgetData = (
  widgetId: number | null,
  input: ApiPeopleAnalyticsWidgetDataInput,
  enabled: boolean = true
) => {
  // Пока список виджетов не загружен, id нет: запрос не уходит, ключ временный
  return useQuery({
    ...peopleAnalyticsKeys.widgetData(widgetId ?? 0, input),
    enabled: enabled && widgetId !== null,
  });
};

export const usePeopleAnalyticsCacheState = () => {
  return useQuery({ ...peopleAnalyticsKeys.cacheState });
};

// После пересборки все отчёты и виджеты страницы пересчитываются из нового кэша
export const useRebuildPeopleAnalyticsCache = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async () => {
      const result = await peopleAnalyticsApi.rebuildCache();
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: peopleAnalyticsKeys._def });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};
